import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import Link from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import HomeIcon from '@material-ui/icons/Home'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useFetchCurrentBreadCrumbQuery } from 'src/redux/api/graphql/commonUiApi'
import { useFetchUserInfoQuery } from '@/redux/api/graphql/userInfoApi'
import { useAppSelector } from '@/utils/hooks/reduxHooks'
import i18n from '../../../i18n/i18n'

const useStyles = makeStyles((theme) => ({
	breadcrumbFontSize: {
		color: '#364263',
		fontSize: '0.875rem',
		display: 'flex',
		alignItems: 'center',
		[theme.breakpoints.down('md')]: {
			fontSize: '0.775rem',
		},
		[theme.breakpoints.down('sm')]: {
			fontSize: '0.675rem',
		},
		[theme.breakpoints.down('xs')]: {
			fontSize: '0.475rem',
		},
		'&:hover': {
			cursor: 'pointer',
		},
	},
	breadcrumbNextIcon: {
		fontSize: '1.2rem',
		[theme.breakpoints.down('sm')]: {
			fontSize: '1rem',
		},
	},
	lastCrumb: {
		fontWeight: 500,
		fontSize: '1rem',
	},
}))

type BreadcrumbItem = { route: string; label: string }

const tClients = i18n.getFixedT(null, 'clients')

function ChedarBreadCrumbs() {
	const classes = useStyles()
	const navigate = useNavigate()
	const location = useLocation()

	const userInfo = useAppSelector((state) => state.auth.userInfo)
	const userInfoId = userInfo.id

	const { data: userInfoData } = useFetchUserInfoQuery(
		{ userInfoId: userInfoId ?? '' },
		{
			skip: !userInfoId,
		}
	)

	// Fetch computed breadcrumbs for current location via RTK Query
	const { data: fetchedCrumbs = [] } = useFetchCurrentBreadCrumbQuery(
		{ currentLocation: location.pathname, userInfoData: userInfoData! },
		{
			skip:
				location.pathname === '/login' ||
				location.pathname === '/clients' ||
				location.pathname === '/freelancer' ||
				location.pathname === '/companyTemplate' ||
				!userInfoData,
		}
	)

	const breadcrumbRoutes: BreadcrumbItem[] = useMemo(() => {
		if (location.pathname === '/login') return []

		if (location.pathname === '/clients') {
			return [
				{
					route: '/clients',
					label: tClients('home'),
				},
			]
		}

		if (
			location.pathname === '/freelancer' ||
			location.pathname === '/companyTemplate'
		) {
			return [
				{
					route: '/freelancer',
					label: tClients('home'),
				},
			]
		}

		if (!fetchedCrumbs || fetchedCrumbs.length === 0) {
			// Default to just Home when nothing else to show
			return [{ route: '/', label: tClients('home') }]
		}

		return [{ route: '/', label: tClients('home') }, ...fetchedCrumbs]
	}, [location, fetchedCrumbs, i18n.language])

	const numberOfBreadcrumbs = breadcrumbRoutes.length

	const breadCrumbClick = (b: BreadcrumbItem) => {
		navigate(b.route)
	}

	if (breadcrumbRoutes.length > 0) {
		return (
			<Breadcrumbs
				separator={
					<NavigateNextIcon className={classes.breadcrumbNextIcon} />
				}
				aria-label="breadcrumb"
			>
				{breadcrumbRoutes.map((b, i) => {
					if (!b) return null
					const isLast =
						numberOfBreadcrumbs !== 1 &&
						i === numberOfBreadcrumbs - 1
					if (isLast)
						return (
							<Typography
								key={i}
								color="textPrimary"
								className={clsx(classes.breadcrumbFontSize, {
									[classes.lastCrumb]:
										i === breadcrumbRoutes.length - 1,
								})}
							>
								{b.label}
							</Typography>
						)
					return (
						<Link
							color="inherit"
							onClick={() => breadCrumbClick(b)}
							key={i}
							className={clsx(classes.breadcrumbFontSize, {
								[classes.lastCrumb]:
									i === breadcrumbRoutes.length - 1,
							})}
						>
							{i === 0 && (
								<HomeIcon
									fontSize="small"
									style={{
										fontSize: '1rem',
										marginRight: '0.2rem',
									}}
								/>
							)}
							{b.label}
						</Link>
					)
				})}
			</Breadcrumbs>
		)
	}
	return null
}

export default ChedarBreadCrumbs
